// R22 — THE MOBILE SHELL, AND THE TAB-HIGHLIGHT LAW THAT ONLY A BROWSER HAS EVER CHECKED.
//
// M3's bottom tab bar and its Rooms sheet: thumb-first, gold-on-black, active tab lit. Mobile-only
// presentation (CSS hides them on desktop); the navigation underneath is the same goTo /
// requestMemberAccess the rest of the app uses, so there is one source of truth.
//
// WHY THESE TWO CAME OUT (roadmap 7's real argument, R9/R10/R11's version of it): they hold no
// state and call no hook, so a gate can CALL them — and what they carry is not markup, it is two
// decisions that can be silently wrong on a phone and look fine on the desk:
//
//   • N9-1's HIGHLIGHT LAW, the owner's ruling, and she photographed the half of it that was
//     failing: while a tapped sheet is open, that sheet's tab owns the highlight OUTRIGHT — not the
//     tab for the page still sitting underneath it. `litTab` is that law, and it is arithmetic now
//     rather than a thing you have to open a phone to see.
//   • `MOBILE_ROOM_PAGES`, which is an R17-shaped roster: the pages that light the Rooms tab. A room
//     missing from it goes dark while the member is standing IN that room. No error, no log — the
//     bar just quietly stops agreeing with where she is.
//
// The events live here too, because the bar is what listens for one and what broadcasts the other,
// and a constant declared 2,600 lines away from its only two readers is how a rename loses one.
import { DoorOpen, House, MessageCircle, Sofa, UserRound, X } from "lucide-react";
import { MenuIcon } from "./MenuIcon";
import { homeMenuItems } from "../catalogs/rooms";
import { PAGE_TITLES, type Page } from "../lib/pages";
import { t } from "../strings.js";
import { DEFAULT_LANGUAGE } from "../language.js";

// The pages that light the Rooms tab. Ask Lala is deliberately NOT here — it owns its own tab.
//
// R22 FOUND A LIVE ONE HERE THE MOMENT THIS BECAME CALLABLE, and it is the R17 shape again: a
// roster one side writes and another branches on, with silence as the default. `living-room` is in
// the Rooms sheet (LR1 put it there, 2026-07-31) and was in NO tab's roster, so a member who tapped
// Rooms, picked the Living Room and landed in it saw **all five tabs dark** — the bar simply stopped
// agreeing with where she was standing. Nothing threw, nothing logged. The fixture holds the general
// law now rather than this one row: no page the sheet can reach may leave the whole bar unlit.
//
// **W14-1a — AND THE SAME ROW HAD TO COME BACK OUT THE DAY THE ROOM GOT ITS OWN TAB.** R22's fix put
// `living-room` here; her W14 bar gives that room slot four. Left in both, a member standing in the
// Living Room would light TWO tabs at once — the exact inverse of the defect R22 closed, arriving
// through R22's own fix. The precedent is three lines up: Ask Lala owns its own tab and is
// deliberately absent from this roster. **So the law is a BICONDITIONAL now** — no page the sheet can
// reach leaves the bar dark, AND no page lights two tabs — because one law and a hole is what let
// this row be wrong in both directions.
export const MOBILE_ROOM_PAGES: Page[] = ["spell-book", "library", "seals", "numerology", "astrology", "tarot", "human-design", "astrocartography", "akashic", "dreams", "palmistry", "daily"];

// W14-2 — THE ROOMS SHEET'S ORDER, DECLARED AS DATA RATHER THAN AS THE SHAPE OF THE JSX.
//
// **THE SHEET WAS MISSING MY ROOM, WHICH IS THE ONE ROOM THAT IS HERS**, and the cost was written in
// this repo already: `src/catalogs/rooms.tsx` states LR1-13's ruling in its own comment — *"THE LIVING
// ROOM SITS DIRECTLY UNDER 'MY ROOM' ... The same array drives the mobile Rooms sheet, which puts it
// first there too"* — and that sentence is TRUE on the desktop sidebar (where Home and My Room are the
// primary block above this array) and FALSE on the phone, where My Room was in the sheet at all. A
// comment stating a law that holds on one surface and not the other is R23/R26/R36's shape exactly.
//
// Her order: **Home, My Room, The Living Room, then the practice rooms as they stand.** The head is
// PINNED and the tail is DERIVED from the room catalog, so a room added tomorrow joins by existing
// (R17) and cannot be added to the sheet twice.
export const MOBILE_SHEET_PINNED: Page[] = ["home", "my-room", "living-room"];
export function mobileSheetPages(): Page[] {
  return [...MOBILE_SHEET_PINNED, ...homeMenuItems.map((r) => r.page).filter((p) => !MOBILE_SHEET_PINNED.includes(p))];
}

// ---------------------------------------------------------------------------
// **HELP-ON-MOBILE (her launch-day order, 2026-08-08) — A MEMBER ON A PHONE HAD NO PATH TO HELP.**
//
// The sheet ended at the last practice room. Support, Terms, Privacy and the FAQs existed only in the
// DESKTOP footer, which the wrapped app never shows — so on a phone there was no door to any of them
// at all. **It is not a hypothetical: a member asked "Where is support is it in the app" the same day,
// and the inbox pass answered "Support is in the menu", which was false on her device.**
//
// **DECLARED AS DATA, DIRECTLY UNDER THE ROOMS, WHICH IS HER WORDS.** The roster is pages the house
// already routes and already titles (`PAGE_TITLES`), so nothing here invents a name or a path — a
// fifth entry joins by being added once. Support leads, because it is the one a member in trouble is
// looking for.
// ---------------------------------------------------------------------------
export const MOBILE_SHEET_HELP: Page[] = ["contact", "terms", "privacy", "faq"];

// Her labels. **TECHNICAL SUPPORT rather than "Contact"**: the page's own title is the generic one it
// wears on the desktop footer, and a member hunting for help scans for the word support (R125 — the
// copy is hers). The rest are her own words from the order.
export const MOBILE_HELP_LABELS: Partial<Record<Page, string>> = {
  contact: "Technical Support",
  terms: "Terms and Conditions",
  privacy: "Privacy Policy",
  faq: "FAQs",
};
export function mobileHelpLabel(page: Page): string {
  return MOBILE_HELP_LABELS[page] || PAGE_TITLES[page];
}
// A room is named with the words the catalog gives it; anything the catalog does not carry falls back
// to the house's own page title (R42's roster), never to a name typed here.
//
// 14-B5 — **THE ROSTER IS WHAT DECIDES WHICH OF THESE WORDS IS A NAME.** A page `homeMenuItems`
// carries is a ROOM and wears the house's own name for it, in any language (ENGLISH_BY_DESIGN's
// "brand." entry, with the reason). The two the roster does not name — `home` and `my-room` — are
// generic doors, so they read from the strings catalog. That is a biconditional rather than a list of
// judgement calls: a room added tomorrow is named by the roster and cannot fall into the translated
// half by being forgotten.
export const NAV_GENERIC_KEYS: Partial<Record<Page, string>> = { home: "nav.home", "my-room": "account.myRoom" };

export function mobileSheetLabel(page: Page, language: string = DEFAULT_LANGUAGE): string {
  const room = homeMenuItems.find((r) => r.page === page);
  if (room) return room.menuTitle || room.title;
  const key = NAV_GENERIC_KEYS[page];
  return key ? t(language, key) : PAGE_TITLES[page];
}

// N9-1 — THE TAB HIGHLIGHT LAW (owner's ruling): the bar highlights the tab the member TAPPED,
// always. Tap Rooms and Rooms is lit while its sheet is up; tap Shelves and Shelves is lit. The
// highlight never stays on the underlying page while a tapped sheet is open.
//
// The page cannot tell us this on its own, because a sheet does not change the page. So an overlay
// that a tab opens ANNOUNCES itself and the bar listens. One event, so a sheet added tomorrow joins
// the law by dispatching it rather than by someone remembering to special-case a fourth tab.
export const OVERLAY_TAB_EVENT = "twelve:overlay-tab";
export function announceOverlayTab(tab: string, open: boolean) {
  window.dispatchEvent(new CustomEvent(OVERLAY_TAB_EVENT, { detail: { tab, open } }));
}

// N1-7: the one announcement the bottom bar makes before it navigates. Every drawer and sheet
// listens; the generated-report modal deliberately does NOT, because the paid-output law says a
// reading a member paid for is dismissed by its own X or Escape, never by a stray tap. In that one
// case the tab still navigates and its page comes up behind the reading.
export const CLOSE_OVERLAYS_EVENT = "twelve:close-overlays";

// HOME-1 FINAL — **THE ONE WAY ANYTHING ON THIS SITE OPENS THE STANDING MENU.**
//
// Her clause 2(d): *"the MENU drops in the SAME format and design as her standing menu."* Her artwork
// PAINTS a menu control, so the tap zone over it has to open the menu the rest of the house already
// has rather than a second one built to sit under her picture — a rebuilt drawer is exactly the
// "reinterpretation" the mockup-matching law refuses, and it would drift from hers on the first
// change to either.
//
// It is an EVENT because `navOpen` is `SiteMenu`'s own state and the home page is not in its tree.
// Lifting that flag up into App.tsx to thread it back down would put the sidebar's open/closed
// decision inside an eleven-thousand-line file no gate can reach (R8/R95); `CauldronDoor`'s own
// shape one door over, and the reason it is that shape.
export const OPEN_SITE_MENU_EVENT = "twelve:open-site-menu";

/** Ask the standing menu to open. One declaration, so a second caller cannot invent a second door. */
export function openSiteMenu(win: Window = window) {
  win.dispatchEvent(new CustomEvent(OPEN_SITE_MENU_EVENT));
}

// THE LAW ITSELF, as a function, so it can be asked every question without a browser. An open sheet
// owns the highlight outright: `byPage` is only consulted when nothing is open.
export const litTab = (tab: string, byPage: boolean, openSheetTab: string | null) =>
  (openSheetTab ? openSheetTab === tab : byPage);

// Does this page light the Rooms tab?
export const roomsTabActive = (page: Page) => MOBILE_ROOM_PAGES.includes(page);

// ITEM 5 — **THE INBOX LIGHTS THE LIVING ROOM TAB**, because it IS the community room's messaging
// and it is reached from that room's own header. Declared as a roster rather than an `||` inside the
// bar's JSX for R22's reason, which this file was written by: a page nobody rostered leaves all five
// tabs dark, and nothing throws. The sheet cannot reach the inbox, so the sheet law would not have
// caught this one — which is exactly why the law is stated per tab and not per sheet.
export const LIVING_ROOM_TAB_PAGES: Page[] = ["living-room", "inbox"];
export const livingRoomTabActive = (page: Page) => LIVING_ROOM_TAB_PAGES.includes(page);

// W14-1, HER ORDER EXACTLY: Home, Rooms, Ask Lala, Living Room, My Room. The Living Room takes slot
// four; Shelves comes out of the bar and stays reachable inside My Room and the rooms as built.
//
// The prop is RENAMED rather than repointed. `onShelves` still opening a room called the Living Room
// would be a handler named for a thing it no longer does, which is the next session's wrong
// assumption — and this file's own header exists because a constant declared far from its readers is
// how a rename loses one.
export function MobileTabBar({ page, openSheetTab, onHome, onRooms, onAskLala, onLivingRoom, onMyRoom, language = DEFAULT_LANGUAGE }: {
  page: Page;
  openSheetTab: string | null;
  onHome: () => void;
  onRooms: () => void;
  onAskLala: () => void;
  onLivingRoom: () => void;
  onMyRoom: () => void;
  language?: string;
}) {
  const lit = (tab: string, byPage: boolean) => litTab(tab, byPage, openSheetTab);
  // N1-7 (owner's ruling): a tab ALWAYS goes where it says. Whatever drawer, sheet or popup is open
  // gets out of the way first — the member should never have to hunt for an X before the bar will
  // work. The announcement goes out before the navigation so the page they asked for is what they
  // land on.
  const go = (action: () => void) => () => {
    window.dispatchEvent(new CustomEvent(CLOSE_OVERLAYS_EVENT));
    action();
  };
  const Tab = (active: boolean, icon: React.ReactElement, label: string, onClick: () => void) => (
    <button
      type="button"
      className={active ? "mobile-tab is-active" : "mobile-tab"}
      onClick={go(onClick)}
      aria-label={label}
      aria-current={active ? "page" : undefined}
    >
      <span className="mobile-tab__icon" aria-hidden="true">{icon}</span>
      <span className="mobile-tab__label">{label}</span>
    </button>
  );
  return (
    // 14-B5: the three DOORS read the catalog; the two ROOMS wear the house's own names, which is the
    // same line `mobileSheetLabel` draws one function up. The Living Room's tab keeps the SHORT form
    // rather than the roster's "The Living Room" — W14-1's sequence is her order and the bar has five
    // labels across 390px — and `r22-mobile-nav.mjs` holds that exact word, which is what caught the
    // first version of this line importing the long one.
    <nav className="mobile-tab-bar" aria-label={t(language, "nav.appNavigation")}>
      {Tab(lit("home", page === "home"), <House size={23} strokeWidth={1.75} />, t(language, "nav.home"), onHome)}
      {Tab(lit("rooms", roomsTabActive(page)), <DoorOpen size={23} strokeWidth={1.75} />, t(language, "nav.rooms"), onRooms)}
      {Tab(lit("ask-lala", page === "ask-lala"), <MessageCircle size={23} strokeWidth={1.75} />, "Ask Lala", onAskLala)}
      {Tab(lit("living-room", livingRoomTabActive(page)), <Sofa size={23} strokeWidth={1.75} />, "Living Room", onLivingRoom)}
      {Tab(lit("my-room", page === "my-room"), <UserRound size={23} strokeWidth={1.75} />, t(language, "account.myRoom"), onMyRoom)}
    </nav>
  );
}

export function MobileRoomsSheet({ currentPage, onClose, onHome, onPick, language = DEFAULT_LANGUAGE }: {
  currentPage: Page;
  onClose: () => void;
  onHome: () => void;
  onPick: (page: Page) => void;
  language?: string;
}) {
  return (
    <div className="mobile-sheet-scrim" onMouseDown={onClose}>
      <div className="mobile-sheet" role="dialog" aria-label={t(language, "nav.rooms")} onMouseDown={(e) => e.stopPropagation()}>
        <div className="mobile-sheet__head">
          <h2>{t(language, "nav.rooms")}</h2>
          <button type="button" className="mobile-sheet__close" onClick={onClose} aria-label={t(language, "nav.closeRooms")}><X size={20} /></button>
        </div>
        <div className="mobile-sheet__list">
          {/* W14-2: one loop over the declared order, so Home stops being a hand-written row that a
              second pinned entry has to be threaded around. Home keeps its own handler because it is
              the one destination that is not a room a member is gated into. */}
          {mobileSheetPages().map((room) => (
            <button
              key={room}
              type="button"
              className={currentPage === room ? "mobile-sheet__item is-active" : "mobile-sheet__item"}
              onClick={room === "home" ? onHome : () => onPick(room)}
            >
              <MenuIcon page={room} /><span>{mobileSheetLabel(room, language)}</span>
            </button>
          ))}

          {/* HELP-ON-MOBILE — her four, directly under the rooms, in the same house styling as every
              row above.

              **THEY LIVE INSIDE THE ROOMS LIST, AND THAT IS THE WHOLE FIX (2026-08-08).** The first
              version made them a SECOND flex child of the sheet, and this list is
              `flex: 1 1 auto; overflow-y: auto` — so the new block took 266px of a 664px sheet and the
              rooms list shrank to 398px against 916px of content. **Nothing was deleted: seven rooms
              were clipped below the fold of a scroll box nobody could tell had shrunk**, and the four
              help rows sat underneath them looking like a replacement. One list, one scroll region,
              everything reachable.

              N10-6's ruling that the sheet ends at the last ROOM is untouched in what it was about:
              the signed-in-as block and Log out are still gone, and Log out still lives only at the
              top under Member. This is a member's path to HELP, which that ruling never spoke to. */}
          <div className="mobile-sheet__rule" aria-hidden="true" />
          {MOBILE_SHEET_HELP.map((page) => (
            <button
              key={page}
              type="button"
              className={currentPage === page ? "mobile-sheet__item is-active" : "mobile-sheet__item"}
              onClick={() => onPick(page)}
            >
              <MenuIcon page={page} /><span>{mobileHelpLabel(page)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
